/**
 * The help controls `POST /handoff` accepts, mirrored from the api's own source [KID-5d § Handover contract].
 *
 * Source: `iss-kiosk-api` `src/modules/handoff/handoff.types.ts` — `HANDOFF_CONTROL`, shipped with KID-5d.
 * ⚠️ **Hand-written, as `enrolment.contract.ts` is, and for the same reason**: types are meant to be generated
 * (convention § 6), and `npm run api:types` reads an `openapi.json` the backend does not emit yet. A value added
 * there is added here in the same change, or the api's `@IsIn` answers `400` to the press it was added for.
 *
 * ⭐ **The kiosk names the CONTROL a candidate took, never a reason or a state** [KOP-1d AC-2]: the server maps it
 * to the hand-off reason and the summon state it writes. No value here equals a hand-off reason or an arrival
 * reason, so neither can ride in under it.
 *
 * It is a module of its own so a screen can name a control without importing the egress.
 */
export const HANDOFF_CONTROL = {
  /**
   * The *Ask for help from a representative* row [0383 row 10; 0220] — named only when the HELD arrival
   * selection is `somethingElse`, never by the screen the row lands on (`FinishScreen` serves other paths too).
   * The api raises *asked for a rep* in the state *asked for help* for it.
   */
  askForHelpRow: 'askForHelpRow',
  /**
   * The pay-card screen's footer *Request assistance* — that button alone [KOP-1g scenario 1; 0383 row 8]. ⛔ The
   * scan layer's *Ask a representative* and the screen's two system hand-offs share its exit and name none
   * [KOP-1g § Open]. The api raises *pay card in person* in the state *asked for help* for it.
   */
  payCardRequestAssistance: 'payCardRequestAssistance',
  /**
   * The associate status screen's *"I can't access my Talent Portal →"* [KOP-1g scenario 2; 0383 row 9]. The api
   * raises *talent portal access* in the state *asked for help* for it.
   */
  talentPortalLink: 'talentPortalLink',
  /**
   * The drug-test screen OPENING — the screen is the ask, and it draws no control [0462; KOP-1g scenario 3; 0383
   * row 7]. The api raises *drug test in person* in the state *asked for help* for it.
   */
  drugTestScreen: 'drugTestScreen',
  /**
   * The submission-failed screen (E4) OPENING — the Bullhorn write failed, and the screen promises a person, so it
   * is the ask and draws no control [0486; KPW-1c]. The api raises *Application not sent* in the state *asked for
   * help* for it [KOP-1d AC-3].
   */
  submissionFailedScreen: 'submissionFailedScreen',
  /**
   * The *Need help?* overlay pressed on a SIGN-IN DEAD END — the lockout, the neutral hand-off and the check-in
   * form's inline lookup error [0171; 0168; 0230 § Decision 1; KID-7 scenario 1; the Operator, 2026-10-14] —
   * and those screens' own help button. Named by `KioskScreen` when its screen says it is one
   * (`signInDeadEnd`). The api raises *stuck at sign-in* in the state *asked for help* for it.
   */
  needHelpAtSignIn: 'needHelpAtSignIn',
  /**
   * The *Need help?* overlay pressed ANYWHERE ELSE [0461 § Decision]. The api raises *asked for a rep* in the
   * state *asked for help* for it.
   */
  needHelpOverlay: 'needHelpOverlay',
} as const;

export type HandoffControl = (typeof HANDOFF_CONTROL)[keyof typeof HANDOFF_CONTROL];
